/**
 * SSE client for streaming responses using native fetch + ReadableStream.
 *
 * Uses fetch instead of EventSource because EventSource only supports
 * GET requests without custom headers. The playground needs POST body
 * + Authorization header.
 */

export interface SSEEventStart {
  type: 'start'
  data: {
    execution_id: string
    agent_id: string
    model: string
    provider: string
  }
}

export interface SSEEventToken {
  type: 'token'
  data: { content: string }
}

export interface SSEEventToolCallStart {
  type: 'tool_call_start'
  data: { name: string; arguments: Record<string, unknown> }
}

export interface SSEEventToolCallEnd {
  type: 'tool_call_end'
  data: {
    name: string
    success: boolean
    result: unknown
    execution_time_ms: number
  }
}

export interface SSEEventUsage {
  type: 'usage'
  data: {
    prompt_tokens: number
    completion_tokens: number
    total_tokens: number
  }
}

export interface SSEEventEnd {
  type: 'end'
  data: {
    execution_time_ms: number
    tools_called: number
    knowledge_chunks_used: number
  }
}

export interface SSEEventError {
  type: 'error'
  data: { message: string; code: string }
}

export type SSEEvent =
  | SSEEventStart
  | SSEEventToken
  | SSEEventToolCallStart
  | SSEEventToolCallEnd
  | SSEEventUsage
  | SSEEventEnd
  | SSEEventError

export interface SSEClientOptions {
  url: string
  body: Record<string, unknown>
  token: string
  signal?: AbortSignal
  onEvent: (event: SSEEvent) => void
  onError?: (error: Error) => void
  onComplete?: () => void
}

function parseSSEChunk(raw: string): SSEEvent | null {
  let eventType = ''
  let data = ''

  for (const line of raw.split('\n')) {
    if (line.startsWith('event: ')) {
      eventType = line.slice(7).trim()
    } else if (line.startsWith('data: ')) {
      data = line.slice(6)
    }
  }

  if (!eventType || !data) return null

  try {
    return { type: eventType, data: JSON.parse(data) } as SSEEvent
  } catch {
    return null
  }
}

export async function streamSSE(options: SSEClientOptions): Promise<void> {
  const { url, body, token, signal, onEvent, onError, onComplete } = options

  let response: Response

  try {
    response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(body),
      signal,
    })
  } catch (err) {
    if (err instanceof DOMException && err.name === 'AbortError') return
    onError?.(err instanceof Error ? err : new Error(String(err)))
    return
  }

  if (!response.ok) {
    let detail = response.statusText
    try {
      const json = await response.json()
      detail = json.detail ?? detail
    } catch {
      // ignore parse errors
    }
    onError?.(new Error(detail))
    return
  }

  const reader = response.body?.getReader()
  if (!reader) {
    onError?.(new Error('Response body is not readable'))
    return
  }

  const decoder = new TextDecoder()
  let buffer = ''

  try {
    let reading = true
    while (reading) {
      const { done, value } = await reader.read()
      if (done) {
        reading = false
        continue
      }

      buffer += decoder.decode(value, { stream: true })

      // Process complete events (delimited by \n\n)
      const parts = buffer.split('\n\n')
      // Last element may be incomplete - keep in buffer
      buffer = parts.pop() ?? ''

      for (const part of parts) {
        if (!part.trim()) continue
        const event = parseSSEChunk(part)
        if (event) onEvent(event)
      }
    }

    // Process any remaining buffer
    if (buffer.trim()) {
      const event = parseSSEChunk(buffer)
      if (event) onEvent(event)
    }

    onComplete?.()
  } catch (err) {
    if (err instanceof DOMException && err.name === 'AbortError') return
    onError?.(err instanceof Error ? err : new Error(String(err)))
  } finally {
    reader.releaseLock()
  }
}
